/*
 * Display helpers. Money arrives as a 2 dp decimal string and is only turned
 * into a number here, at the edge of the UI. Arithmetic on money (the withdraw
 * form's limits) is done in whole paise so 0.1 + 0.2 never shows up.
 */

import type { CommissionProgram, CommissionSlab, Money } from "../api/client";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const INR_WHOLE = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function num(value: Money | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** "₹1,48,250.00". "—" when there is no figure. Never "-₹0.00". */
export function inr(value: Money | number | null | undefined): string {
  const n = num(value);
  if (n === null) return "—";
  return INR.format(Math.round(n * 100) / 100 || 0);
}

/** "₹1,48,250" for places with no room for paise (chart tooltips stay exact). */
export function inrWhole(value: Money | number | null | undefined): string {
  const n = num(value);
  if (n === null) return "—";
  return INR_WHOLE.format(Math.round(n) || 0);
}

/** 1,48,250 -> "₹1.5L". Chart axes only. */
export function inrCompact(value: number): string {
  const units: [number, string][] = [[1e7, "Cr"], [1e5, "L"], [1e3, "K"]];
  for (const [size, suffix] of units) {
    const scaled = Math.round((Math.abs(value) / size) * 10) / 10;
    if (scaled >= 1 && Math.abs(value) >= 1e3) {
      return `${value < 0 ? "−" : ""}₹${scaled % 1 === 0 ? scaled.toFixed(0) : scaled.toFixed(1)}${suffix}`;
    }
  }
  return `₹${Math.round(value)}`;
}

/**
 * "12345678901.00" (rupees) -> "₹1,234.57 cr". 1 crore = 10^7 rupees. Profit share investment only.
 * Clients' net cash can be negative: "−₹0.50 cr", sign first, as inrCompact does. Never "−₹0 cr".
 */
export function inrCrore(value: Money | number | null | undefined): string {
  const n = num(value);
  if (n === null) return "—";
  const cr = Math.round((Math.abs(n) / 1e7) * 100) / 100;
  const digits = cr.toLocaleString("en-IN", { minimumFractionDigits: cr % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  return `${n < 0 && cr > 0 ? "−" : ""}₹${digits} cr`;
}

/** "400.00" -> 40000. Exact for any 2 dp decimal string. */
export function toPaise(value: Money | null | undefined): number {
  if (!value) return 0;
  const m = /^(-)?(\d*)(?:\.(\d{0,2})\d*)?$/.exec(value.trim());
  if (!m) return Math.round(Number(value) * 100) || 0;
  const whole = Number(m[2] || "0");
  const frac = Number((m[3] ?? "").padEnd(2, "0"));
  return (m[1] ? -1 : 1) * (whole * 100 + frac);
}

/** 40000 -> "400.00", the API's money format. */
export function fromPaise(paise: number): Money {
  const sign = paise < 0 ? "-" : "";
  const abs = Math.abs(Math.round(paise));
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/** "18.00" (percent) -> 1800 basis points. */
function bp(rate: string): number {
  return Math.round(Number(rate) * 100) || 0;
}

/** Round-half-up of a / b for non-negative integers, without floats. */
function divRound(a: number, b: number): number {
  return Math.floor((2 * a + b) / (2 * b));
}

export type Breakdown = { taxable: number; gst: number; tds: number; net: number };

/**
 * What a withdrawal of `paise` pays out, in paise. Mirrors the server's
 * app.partner_withdrawal_breakdown(): the amount is GST-inclusive, TDS is on
 * the taxable part, both rounded to the paisa. The server's figures win.
 */
export function withdrawalBreakdown(paise: number, gstRate: string, tdsRate: string): Breakdown {
  const taxable = divRound(paise * 10000, 10000 + bp(gstRate));
  const gst = paise - taxable;
  const tds = divRound(taxable * bp(tdsRate), 10000);
  return { taxable, gst, tds, net: paise - gst - tds };
}

/** "10.00" -> "10%", "7.50" -> "7.5%". */
export function rateLabel(rate: string | null | undefined): string {
  const n = num(rate);
  if (n === null) return "—";
  return `${Number.isInteger(n) ? n.toFixed(0) : String(Math.round(n * 100) / 100)}%`;
}

export type ProgramStatus = {
  /** Payout has started: the partner has more than `activation_clients` clients. */
  active: boolean;
  /** Clients still to onboard before payout starts; 0 once it has. */
  toActivate: number;
  /** The slab the partner is in now, and the next one up (null at the top). */
  slab: CommissionSlab | null;
  next: CommissionSlab | null;
  /** Clients still to onboard to reach `next`. */
  toNext: number;
};

/** Where a partner stands in the tiered programme. The server's `commission_rate` wins for the rate itself. */
export function programStatus(p: CommissionProgram): ProgramStatus {
  const n = p.client_count;
  const slab = p.slabs.find((s) => n >= s.from && (s.to === null || n <= s.to)) ?? null;
  const next = p.slabs.find((s) => s.from > n) ?? null;
  const active = n > p.activation_clients;
  return {
    active,
    toActivate: active ? 0 : p.activation_clients + 1 - n,
    slab,
    next,
    toNext: next ? next.from - n : 0,
  };
}

/** "0–25 clients", "26–99 clients", "100+ clients". */
export function slabLabel(s: CommissionSlab): string {
  return s.to === null ? `${s.from}+ clients` : `${s.from}–${s.to} clients`;
}

/* ---- dates ------------------------------------------------------------ */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The API can send a bare-hour offset ("…+05"), which Date cannot parse. */
function parse(iso: string): Date | null {
  const d = new Date(iso.replace(/([+-]\d{2})$/, "$1:00"));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "12 Oct 2026". A date-only value is shown as that calendar day in any timezone. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = DATE_ONLY.exec(iso);
  if (m) {
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  }
  const d = parse(iso);
  if (!d) return iso.slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

/** "12 Oct 2026, 3:45 pm" (IST). */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  if (DATE_ONLY.test(iso)) return fmtDate(iso);
  const d = parse(iso);
  if (!d) return iso;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
}

/** "3:45 pm" (IST). "" for a date-only or unreadable value. */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso || DATE_ONLY.test(iso)) return "";
  const d = parse(iso);
  return d ? d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "";
}

// Built once: constructing an Intl.DateTimeFormat per row is slow for a 500-entry statement.
const IST_DAY = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" });
const IST_STAMP = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "Asia/Kolkata",
});

/** "2026-10-12 15:45" (IST, 24-hour): sorts and parses cleanly in a spreadsheet. */
export function fmtStamp(iso: string): string {
  const d = parse(iso);
  if (!d) return iso;
  return IST_STAMP.format(d).replace(", ", " ");
}

/** "2026-04" -> "Apr" (short) or "Apr 2026" (long). */
export function fmtMonth(month: string, style: "short" | "long" = "short"): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-IN", {
    month: "short",
    year: style === "long" ? "numeric" : undefined,
    timeZone: "UTC",
  });
}

/** Today in India as YYYY-MM-DD: the calendar the commission filters speak. */
export function todayIst(): string {
  return IST_DAY.format(new Date());
}

/** Calendar arithmetic on a YYYY-MM-DD string; the day is clamped to the month. */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
}

/** The month in India a timestamp falls in, as "YYYY-MM" (for fmtMonth). */
export function istMonth(iso: string): string {
  const d = parse(iso);
  return d ? IST_DAY.format(d).slice(0, 7) : iso.slice(0, 7);
}

/* ---- date ranges (Commissions, Transactions) ---- */

export type RangeKey = "month" | "3m" | "fy" | "all";

export const RANGES: RangeKey[] = ["month", "3m", "fy", "all"];

/** The financial year runs April to March: "FY 2026–27". */
export function fyLabel(): string {
  const [y, m] = todayIst().split("-").map(Number);
  const start = m >= 4 ? y : y - 1;
  return `FY ${start}–${String(start + 1).slice(2)}`;
}

export function rangeLabel(key: RangeKey): string {
  switch (key) {
    case "month":
      return "This month";
    case "3m":
      return "Last 3 months";
    case "fy":
      return `This FY (${fyLabel().slice(3)})`;
    case "all":
      return "All";
  }
}

/** The calendar days in India a range covers, ending today; "all" has no bounds. */
export function rangeFor(key: RangeKey): { from?: string; to?: string } {
  const today = todayIst();
  const [y, m] = today.split("-").map(Number);
  switch (key) {
    case "month":
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case "3m":
      return { from: addMonthsIso(today, -3), to: today };
    case "fy":
      return { from: `${m >= 4 ? y : y - 1}-04-01`, to: today };
    case "all":
      return {};
  }
}

/* ---- people ------------------------------------------------------------ */

export function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/** "Asha Rao" -> "AR". */
export function initials(name: string | null | undefined): string {
  if (!name?.trim()) return "—";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function firstName(name: string | null | undefined): string {
  return name?.trim().split(/\s+/)[0] || "there";
}

/** "past_due" -> "Past due". */
export function humanize(value: string | null | undefined): string {
  if (!value) return "—";
  const s = value.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd". */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  const suffix = rem100 >= 11 && rem100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${suffix}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
